import { EnvironmentProviders, Injectable, inject, makeEnvironmentProviders } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import {
  BehaviorSubject,
  catchError,
  from,
  map,
  NEVER,
  Observable,
  of,
  ReplaySubject,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { APP_ROUTE_URLS } from '../app-config/routing/app-route-paths';
import { AppConfig } from '../../shared/app-config';
import { IUser } from '../../shell/authorization/models/user.interface';
import { UserRole } from '../../shell/authorization/models/user-role.enum';
import { AUTH_PROVIDER, AuthProviderService } from './auth-provider.interface';

interface ZitadelTokenResponse {
  access_token: string;
  token_type: string;
  expires_in: number;
  id_token: string;
}

interface ZitadelUserInfo {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  given_name?: string;
  family_name?: string;
}

type JwtClaims = Record<string, unknown>;

const ZITADEL_DEFAULT_ROLES_CLAIM = 'urn:zitadel:iam:org:project:roles';
const ZITADEL_PROJECT_ROLES_SCOPE = 'urn:zitadel:iam:org:projects:roles';
const ZITADEL_ADMIN_ROLE = 'admin';

const ZITADEL_ACCESS_TOKEN_STORAGE_KEY = 'zitadel_access_token';
const ZITADEL_ID_TOKEN_STORAGE_KEY = 'zitadel_id_token';
const ZITADEL_PKCE_VERIFIER_STORAGE_KEY = 'zitadel_pkce_code_verifier';

const createPkceVerifier = (): string => {
  const bytes = new Uint8Array(32);
  window.crypto.getRandomValues(bytes);
  return base64UrlEncode(bytes);
};

const createPkceChallenge = async (verifier: string): Promise<string> => {
  const data = new TextEncoder().encode(verifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(new Uint8Array(digest));
};

const base64UrlEncode = (bytes: Uint8Array): string => {
  return window
    .btoa(String.fromCharCode(...bytes))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
};

const parseJwtClaims = (token: string): JwtClaims | null => {
  const tokenParts = token.split('.');
  if (tokenParts.length !== 3) {
    return null;
  }

  try {
    const payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
    const paddedPayload = payload.padEnd(payload.length + ((4 - (payload.length % 4)) % 4), '=');
    const claimsJson = window.atob(paddedPayload);
    return JSON.parse(claimsJson) as JwtClaims;
  } catch {
    return null;
  }
};

const extractRoleKeys = (claims: JwtClaims | null, projectId: string): string[] => {
  if (!claims) {
    return [];
  }

  const claimCandidates = [`urn:zitadel:iam:org:project:${projectId}:roles`, ZITADEL_DEFAULT_ROLES_CLAIM, 'role'];
  const roleKeys = new Set<string>();

  for (const claimName of claimCandidates) {
    const claimValue = claims[claimName];
    if (!claimValue) {
      continue;
    }

    if (typeof claimValue === 'string') {
      roleKeys.add(claimValue);
      continue;
    }

    if (Array.isArray(claimValue)) {
      claimValue.forEach((value) => {
        if (typeof value === 'string') {
          roleKeys.add(value);
        }
      });
      continue;
    }

    if (typeof claimValue === 'object') {
      Object.keys(claimValue as Record<string, unknown>).forEach((role) => roleKeys.add(role));
    }
  }

  return Array.from(roleKeys);
};

const mapRoleKeysToUserRoles = (roleKeys: string[]): UserRole[] => {
  const normalizedRoleKeys = roleKeys.map((roleKey) => roleKey.toLowerCase());
  const userRoles: UserRole[] = [];

  if (normalizedRoleKeys.includes(ZITADEL_ADMIN_ROLE)) {
    userRoles.push(UserRole.Administrator);
  }

  if (normalizedRoleKeys.includes('operator')) {
    userRoles.push(UserRole.Operator);
  }

  return userRoles;
};

const mapZitadelUserToAppUser = (userInfo: ZitadelUserInfo, roleKeys: string[]): IUser => {
  return {
    firstname: userInfo.given_name || userInfo.name || 'User',
    lastname: userInfo.family_name || '',
    email: userInfo.email || '',
    roles: mapRoleKeysToUserRoles(roleKeys),
    token: '',
  };
};

/**
 * Zitadel Authentication provider.
 * Handles OIDC-based login/logout and token acquisition via OAuth 2.0 authorization code flow with PKCE.
 */
@Injectable()
export class ZitadelAuthService implements AuthProviderService {
  private readonly _http = inject(HttpClient);
  private _currentUser: IUser | null = null;
  private readonly _currentUser$ = new ReplaySubject<IUser | null>(1);
  private readonly _accessToken$ = new BehaviorSubject<string | null>(null);

  constructor() {
    this._initializeSession();
  }

  private _initializeSession(): void {
    if (this._isAuthorizationCallbackRoute()) {
      this._completeAuthorizationCallback();
      return;
    }

    if (this._isLogoutCallbackRoute()) {
      this.clearSession();
      return;
    }

    const storedToken = sessionStorage.getItem(ZITADEL_ACCESS_TOKEN_STORAGE_KEY);
    if (!storedToken) {
      this._setCurrentUser(null);
      return;
    }

    this._accessToken$.next(storedToken);
    const storedRoleKeys = this._extractRoleKeysFromTokens(storedToken);
    this._getUserInfo(storedToken).subscribe({
      next: (userInfo) => {
        this._setCurrentUser(mapZitadelUserToAppUser(userInfo, storedRoleKeys));
      },
      error: () => {
        this.clearSession();
      },
    });
  }

  private _isAuthorizationCallbackRoute(): boolean {
    const config = AppConfig.zitadel;
    if (!config) {
      return false;
    }

    return this._matchesCurrentPath(config.redirectUri);
  }

  private _isLogoutCallbackRoute(): boolean {
    const config = AppConfig.zitadel;
    if (!config?.postLogoutRedirectUri) {
      return false;
    }

    return this._matchesCurrentPath(config.postLogoutRedirectUri);
  }

  private _matchesCurrentPath(url: string): boolean {
    try {
      const configured = this._toAbsoluteUrl(url);
      const configuredPath = new URL(configured).pathname;
      return configuredPath === window.location.pathname;
    } catch {
      return false;
    }
  }

  private _completeAuthorizationCallback(): void {
    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const error = params.get('error');

    if (!code || error || !AppConfig.zitadel?.clientId) {
      this._setCurrentUser(null);
      this._cleanupCallbackUrl(window.location.pathname);
      return;
    }

    this._exchangeCodeForToken(code).subscribe({
      next: ({ userInfo, roleKeys }) => {
        this._setCurrentUser(mapZitadelUserToAppUser(userInfo, roleKeys));
        this._cleanupCallbackUrl(window.location.pathname);
      },
      error: () => {
        this._setCurrentUser(null);
        this._cleanupCallbackUrl(window.location.pathname);
      },
    });
  }

  private _cleanupCallbackUrl(pathname: string): void {
    window.history.replaceState({}, document.title, pathname);
  }

  private _exchangeCodeForToken(code: string): Observable<{
    userInfo: ZitadelUserInfo;
    roleKeys: string[];
  }> {
    const config = AppConfig.zitadel;
    if (!config) {
      throw new Error('Zitadel config not provided in AppConfig');
    }

    const tokenEndpoint = `${config.issuer}/oauth/v2/token`;
    const codeVerifier = sessionStorage.getItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
    if (!codeVerifier) {
      throw new Error('Zitadel PKCE verifier not found');
    }

    const body = new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      client_id: config.clientId,
      code_verifier: codeVerifier,
      redirect_uri: config.redirectUri,
    });

    return this._http
      .post<ZitadelTokenResponse>(tokenEndpoint, body.toString(), {
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      })
      .pipe(
        tap((response) => {
          this._accessToken$.next(response.access_token);
          sessionStorage.setItem(ZITADEL_ACCESS_TOKEN_STORAGE_KEY, response.access_token);
          sessionStorage.setItem(ZITADEL_ID_TOKEN_STORAGE_KEY, response.id_token);
          sessionStorage.removeItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
        }),
        switchMap((response) => {
          const roleKeys = this._extractRoleKeysFromTokens(response.access_token, response.id_token);
          return this._getUserInfo(response.access_token).pipe(map((userInfo) => ({ userInfo, roleKeys })));
        }),
        catchError((error) => {
          return throwError(() => error);
        })
      );
  }

  private _extractRoleKeysFromTokens(accessToken: string, idToken?: string): string[] {
    const config = AppConfig.zitadel;
    if (!config) {
      return [];
    }

    const claimsCandidates = [parseJwtClaims(idToken ?? ''), parseJwtClaims(accessToken)].filter(
      (claims): claims is JwtClaims => claims !== null
    );

    for (const claims of claimsCandidates) {
      const roleKeys = extractRoleKeys(claims, config.projectId);
      if (roleKeys.length > 0) {
        return roleKeys;
      }
    }

    return [];
  }

  private _getUserInfo(accessToken: string): Observable<ZitadelUserInfo> {
    const config = AppConfig.zitadel;
    if (!config) {
      throw new Error('Zitadel config not provided');
    }

    const userInfoEndpoint = `${config.issuer}/oidc/v1/userinfo`;
    return this._http.get<ZitadelUserInfo>(userInfoEndpoint, {
      headers: { Authorization: `Bearer ${accessToken}` },
    });
  }

  currentUser(): IUser | null {
    return this._currentUser;
  }

  sessionChanges$(): Observable<IUser | null> {
    return this._currentUser$.asObservable();
  }

  accessToken$(resourceUrl: string): Observable<string | null> {
    void resourceUrl;
    const storedToken = sessionStorage.getItem(ZITADEL_ACCESS_TOKEN_STORAGE_KEY);
    if (storedToken) {
      return of(storedToken);
    }
    return this._accessToken$.asObservable();
  }

  login$(): Observable<string | undefined> {
    const config = AppConfig.zitadel;
    if (!config) {
      return of('authentication.providerNotInitialized');
    }

    try {
      const codeVerifier = createPkceVerifier();
      sessionStorage.setItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY, codeVerifier);
      return from(this._redirectToAuthorizationEndpoint(config, codeVerifier)).pipe(
        map(() => undefined),
        catchError((error) => {
          void error;
          sessionStorage.removeItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
          return of('authentication.loginFailed');
        })
      );
    } catch {
      sessionStorage.removeItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
      return of('authentication.loginFailed');
    }
  }

  private async _redirectToAuthorizationEndpoint(
    config: NonNullable<typeof AppConfig.zitadel>,
    codeVerifier: string
  ): Promise<void> {
    const codeChallenge = await createPkceChallenge(codeVerifier);
    const parameters = new URLSearchParams({
      client_id: config.clientId,
      redirect_uri: config.redirectUri,
      response_type: 'code',
      scope: this._resolveScopes(config).join(' '),
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
    });
    const authorizationUrl = `${config.issuer}/oauth/v2/authorize?${parameters.toString()}`;

    window.location.assign(authorizationUrl);
  }

  private _resolveScopes(config: NonNullable<typeof AppConfig.zitadel>): string[] {
    if (config.scopes && config.scopes.length > 0) {
      return config.scopes;
    }

    return [
      'openid',
      'profile',
      'email',
      'offline_access',
      `urn:zitadel:iam:org:project:id:${config.projectId}:aud`,
      ZITADEL_PROJECT_ROLES_SCOPE,
    ];
  }

  private _toAbsoluteUrl(url: string): string {
    return new URL(url, window.location.origin).toString();
  }

  logout$(): Observable<void> {
    const config = AppConfig.zitadel;
    if (!config) {
      return of(undefined);
    }

    const idTokenHint = sessionStorage.getItem(ZITADEL_ID_TOKEN_STORAGE_KEY);

    sessionStorage.removeItem(ZITADEL_ACCESS_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ZITADEL_ID_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
    this._setCurrentUser(null);
    this._accessToken$.next(null);

    try {
      const configuredPostLogoutRedirect = config.postLogoutRedirectUri ?? APP_ROUTE_URLS.authLogoutCallback;
      const postLogoutRedirectUri = this._toAbsoluteUrl(configuredPostLogoutRedirect);
      const logoutParameters = new URLSearchParams({
        post_logout_redirect_uri: postLogoutRedirectUri,
      });
      if (idTokenHint) {
        logoutParameters.set('id_token_hint', idTokenHint);
      }
      const logoutUrl = `${config.issuer}/oidc/v1/end_session?${logoutParameters.toString()}`;
      window.location.assign(logoutUrl);
      return NEVER;
    } catch {
      window.location.assign(APP_ROUTE_URLS.login);
      return NEVER;
    }
  }

  clearSession(): void {
    sessionStorage.removeItem(ZITADEL_ACCESS_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ZITADEL_ID_TOKEN_STORAGE_KEY);
    sessionStorage.removeItem(ZITADEL_PKCE_VERIFIER_STORAGE_KEY);
    this._setCurrentUser(null);
    this._accessToken$.next(null);
  }

  private _setCurrentUser(user: IUser | null): void {
    this._currentUser = user;
    this._currentUser$.next(user);
  }
}

/**
 * Provides Zitadel Authentication to the application.
 * Include this provider in your app's providers array to enable Zitadel login.
 *
 * Requires Zitadel config in AppConfig.zitadel.
 *
 * @example
 * bootstrapApplication(AppComponent, {
 *   providers: [
 *     provideZitadelAuth(),
 *     // ... other providers
 *   ]
 * });
 */
export function provideZitadelAuth(): EnvironmentProviders {
  return makeEnvironmentProviders([
    ZitadelAuthService,
    {
      provide: AUTH_PROVIDER,
      useExisting: ZitadelAuthService,
    },
  ]);
}
